import { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Button,
  SimpleGrid,
  createPolymorphicComponent,
} from '@mantine/core';
import type { TextProps } from '@mantine/core';
import {
  IconCode,
  IconMessageChatbot,
  IconMail,
  IconBulb,
  IconRocket,
  IconBrush,
  IconTestPipe,
  IconQuote,
} from '@tabler/icons-react';
import { Link, useParams } from 'react-router';
import { BackgroundGradient } from '@epihub/web/src/tailwind/components/BackgroundGradient';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { TextGenerateEffect } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import { EncryptedText } from '@epihub/web/src/tailwind/components/EncryptedText';
import { StickyScrollReveal } from '@epihub/web/src/tailwind/components/StickyScrollReveal';
import { Globe3D } from '@epihub/web/src/tailwind/components/Globe3D';
import { styled, useTheme } from '@epihub/web/src/_ctx/theme';
import { assets } from '../assets';
import { useSfyr } from '../_ctx/sfyr';
import { type BlogPostMetadata, getFeaturedPosts, getAllPosts } from '../blog/posts';

const HeroSection = styled.section`
  min-height: 90vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const SectionPadded = styled.section`
  padding: 60px 0;
  @media (min-width: 768px) {
    padding: 100px 0;
  }
`;

const SectionLabel = createPolymorphicComponent<'p', TextProps>(styled(Text)`
  letter-spacing: 2px;
`);

const AboutParagraph = createPolymorphicComponent<'p', TextProps>(styled(Text)`
  line-height: 1.8;
`);

const TestimonialQuote = createPolymorphicComponent<'p', TextProps>(styled(Text)`
  line-height: 1.8;
  font-style: italic;
`);

const TestimonialAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
`;

const CtaSubtitle = createPolymorphicComponent<'p', TextProps>(styled(Text)`
  line-height: 1.7;
`);

const HeroSubtitle = createPolymorphicComponent<'p', TextProps>(styled(Text)`
  line-height: 1.7;
`);

const BlogCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s;
`;

const heroWordsEn = [
  { text: 'We' },
  { text: 'turn' },
  { text: 'your' },
  { text: 'ideas', style: { fontWeight: 700 } as const },
  { text: 'into', style: { fontWeight: 700 } as const },
  { text: 'digital', style: { fontWeight: 700 } as const },
  { text: 'reality.', style: { fontWeight: 700 } as const },
];

const heroWordsIt = [
  { text: 'Trasformiamo' },
  { text: 'le' },
  { text: 'tue' },
  { text: 'idee', style: { fontWeight: 700 } as const },
  { text: 'in', style: { fontWeight: 700 } as const },
  { text: 'realtà', style: { fontWeight: 700 } as const },
  { text: 'digitale.', style: { fontWeight: 700 } as const },
];

export const LandingPage = () => {
  const theme = useTheme();
  const sfyr = useSfyr();
  const { lang } = useParams<{ lang: string }>();
  const langPrefix = lang ? `/${lang}` : '';
  const heroWords = lang === 'it' ? heroWordsIt : heroWordsEn;
  const [featuredPosts, setFeaturedPosts] = useState<BlogPostMetadata[]>([]);
  const [totalPostCount, setTotalPostCount] = useState(0);

  useEffect(() => {
    const locale = lang === 'it' ? 'it' : 'en';
    getFeaturedPosts({ lang: locale }).then(setFeaturedPosts);
    getAllPosts(locale).then((posts) => setTotalPostCount(posts.length));
  }, [lang]);

  const workflowSteps = [
    {
      title: sfyr('landing.{lang}.workflow.step_1.title', {
        defaults: { 'landing.{lang=en}.workflow.step_1.title': 'Analysis & Concept' },
      }),
      description: sfyr('landing.{lang}.workflow.step_1.description', {
        defaults: {
          'landing.{lang=en}.workflow.step_1.description': 'Requirements, scope, and a clear quote.',
        },
      }),
      content: (
        <div className='flex h-full w-full items-center justify-center'>
          <IconBulb size={80} color='white' opacity={0.6} />
        </div>
      ),
    },
    {
      title: sfyr('landing.{lang}.workflow.step_2.title', {
        defaults: { 'landing.{lang=en}.workflow.step_2.title': 'Design & Prototyping' },
      }),
      description: sfyr('landing.{lang}.workflow.step_2.description', {
        defaults: {
          'landing.{lang=en}.workflow.step_2.description':
            'Wireframes and interactive prototypes, validated with your feedback.',
        },
      }),
      content: (
        <div className='flex h-full w-full items-center justify-center'>
          <IconBrush size={80} color='white' opacity={0.6} />
        </div>
      ),
    },
    {
      title: sfyr('landing.{lang}.workflow.step_3.title', {
        defaults: { 'landing.{lang=en}.workflow.step_3.title': 'Development' },
      }),
      description: sfyr('landing.{lang}.workflow.step_3.description', {
        defaults: {
          'landing.{lang=en}.workflow.step_3.description':
            'We build it. Regular demos so you always know where things stand.',
        },
      }),
      content: (
        <div className='flex h-full w-full items-center justify-center'>
          <IconCode size={80} color='white' opacity={0.6} />
        </div>
      ),
    },
    {
      title: sfyr('landing.{lang}.workflow.step_4.title', {
        defaults: { 'landing.{lang=en}.workflow.step_4.title': 'Testing & QA' },
      }),
      description: sfyr('landing.{lang}.workflow.step_4.description', {
        defaults: {
          'landing.{lang=en}.workflow.step_4.description':
            'Tested across platforms for functionality, performance, and security.',
        },
      }),
      content: (
        <div className='flex h-full w-full items-center justify-center'>
          <IconTestPipe size={80} color='white' opacity={0.6} />
        </div>
      ),
    },
    {
      title: sfyr('landing.{lang}.workflow.step_5.title', {
        defaults: { 'landing.{lang=en}.workflow.step_5.title': 'Launch & Support' },
      }),
      description: sfyr('landing.{lang}.workflow.step_5.description', {
        defaults: {
          'landing.{lang=en}.workflow.step_5.description': 'Deployed and supported. Updates and maintenance included.',
        },
      }),
      content: (
        <div className='flex h-full w-full items-center justify-center'>
          <IconRocket size={80} color='white' opacity={0.6} />
        </div>
      ),
    },
  ];

  return (
    <div style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
      <HeroSection>
        <Container size='lg' style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ padding: '48px 0' }}>
            <div style={{ maxWidth: 600 }}>
              <TextGenerateEffect
                words={heroWords}
                defaultMsPerWord={120}
                className='text-4xl md:text-6xl font-light tracking-tight'
              />
            </div>

            <HeroSubtitle
              size='xl'
              c={theme.v0.col.textSecondary.hex}
              maw={480}
              mt='md'
            >
              {sfyr('landing.{lang}.hero.subtitle_prefix', {
                defaults: {
                  'landing.{lang=en}.hero.subtitle_prefix': 'Web, mobile, AI — we design and ship ',
                  'landing.{lang=it}.hero.subtitle_prefix': 'Web, mobile, AI — progettiamo e realizziamo ',
                },
              })}
              <EncryptedText
                text={sfyr('landing.{lang}.hero.subtitle_highlight', {
                  defaults: {
                    'landing.{lang=en}.hero.subtitle_highlight': 'software that works',
                    'landing.{lang=it}.hero.subtitle_highlight': 'software che funziona',
                  },
                })}
                revealDelayMs={40}
                encryptedClassName='opacity-40'
              />
              {sfyr('landing.{lang}.hero.subtitle_suffix', {
                defaults: {
                  'landing.{lang=en}.hero.subtitle_suffix': '.',
                  'landing.{lang=it}.hero.subtitle_suffix': '.',
                },
              })}
            </HeroSubtitle>

            <Group mt='xl' gap='lg'>
              <a href='mailto:info@epilogo.co?subject=Quote%20request' style={{ textDecoration: 'none' }}>
                <HoverBorderGradient
                  containerClassName='rounded-full'
                  className='flex items-center gap-2 px-8 py-3 cursor-pointer'
                >
                  <span className='text-base font-medium'>
                    {sfyr('landing.{lang}.hero.cta_quote', {
                      defaults: { 'landing.{lang=en}.hero.cta_quote': 'Request a quote' },
                    })}
                  </span>
                </HoverBorderGradient>
              </a>
            </Group>
          </div>
        </Container>
        <div className='absolute -right-40 -bottom-60 z-0 size-80 sm:-right-72 sm:-bottom-96 sm:size-160 md:-bottom-80 md:size-180'>
          <Globe3D
            className='h-full w-full'
            markers={[
              { lat: 45.5416, lng: 10.2118, src: assets.LogoSquare.url, label: 'Brescia' },
            ]}
            config={{
              atmosphereColor: '#7c3aed',
              atmosphereIntensity: 0.3,
              showAtmosphere: true,
              bumpScale: 5,
              autoRotateSpeed: 0.3,
              enableZoom: false,
              enablePan: false,
            }}
          />
        </div>
      </HeroSection>

      <SectionPadded
        id='about'
        style={{ borderTop: `1px solid ${theme.v0.col.backgroundTertiary.hex}` }}
      >
        <Container size='md'>
          <SectionLabel
            size='sm'
            fw={700}
            tt='uppercase'
            c={theme.v0.col.primary.hex}
            ta='center'
            mb='sm'
          >
            {sfyr('landing.{lang}.about.label', { defaults: { 'landing.{lang=en}.about.label': 'About' } })}
          </SectionLabel>
          <Title order={2} ta='center' mb='md'>
            {sfyr('landing.{lang}.about.title', {
              defaults: { 'landing.{lang=en}.about.title': 'Who we are' },
            })}
          </Title>
          <AboutParagraph c={theme.v0.col.textSecondary.hex} size='lg' ta='center'>
            {sfyr('landing.{lang}.about.paragraph_1', {
              defaults: {
                'landing.{lang=en}.about.paragraph_1':
                  'We build websites, platforms, and AI-powered tools. We work closely with you to understand the problem and ship the right solution.',
              },
            })}
          </AboutParagraph>
        </Container>
      </SectionPadded>

      <SectionPadded>
        <Container size='lg' mb='xl'>
          <SectionLabel
            size='sm'
            fw={700}
            tt='uppercase'
            c={theme.v0.col.primary.hex}
            ta='center'
            mb='sm'
          >
            {sfyr('landing.{lang}.workflow.label', {
              defaults: { 'landing.{lang=en}.workflow.label': 'How we work' },
            })}
          </SectionLabel>
          <Title order={2} ta='center' mb='sm'>
            {sfyr('landing.{lang}.workflow.title', {
              defaults: { 'landing.{lang=en}.workflow.title': 'Our process' },
            })}
          </Title>
        </Container>
        <StickyScrollReveal content={workflowSteps} />
      </SectionPadded>

      <SectionPadded>
        <Container size='sm'>
          <Stack align='center' gap='lg'>
            <IconQuote size={40} color={theme.v0.col.primary.rgba(0.3)} />
            <TestimonialQuote ta='center' size='xl' c={theme.v0.col.text.hex}>
              {sfyr('landing.{lang}.testimonial.quote', {
                defaults: {
                  'landing.{lang=en}.testimonial.quote':
                    'Always very attentive to my needs and ready to propose intelligent solutions. I found a lot of flexibility and transparency in the way of working.',
                },
              })}
            </TestimonialQuote>
            <div style={{ textAlign: 'center' }}>
              <TestimonialAvatar style={{ backgroundColor: theme.v0.col.primary.rgba(0.13) }}>
                <Text fw={700} c={theme.v0.col.primary.hex}>GP</Text>
              </TestimonialAvatar>
              <Text fw={600}>
                {sfyr('landing.{lang}.testimonial.name', {
                  defaults: { 'landing.{lang=en}.testimonial.name': 'Giorgia P.' },
                })}
              </Text>
              <Text size='sm' c={theme.v0.col.textDimmed.hex}>
                {sfyr('landing.{lang}.testimonial.role', {
                  defaults: { 'landing.{lang=en}.testimonial.role': 'Founder @ Penelope Skin' },
                })}
              </Text>
            </div>
          </Stack>
        </Container>
      </SectionPadded>

      {featuredPosts.length > 0 && (
        <SectionPadded>
          <Container size='lg'>
            <SectionLabel
              size='sm'
              fw={700}
              tt='uppercase'
              c={theme.v0.col.primary.hex}
              ta='center'
              mb='sm'
            >
              {sfyr('landing.{lang}.blog.label', {
                defaults: { 'landing.{lang=en}.blog.label': 'Blog' },
              })}
            </SectionLabel>
            <Title order={2} ta='center' mb='sm'>
              {sfyr('landing.{lang}.blog.title', {
                defaults: { 'landing.{lang=en}.blog.title': 'Latest from the blog' },
              })}
            </Title>
            <Text ta='center' c={theme.v0.col.textSecondary.hex} mb='xl' maw={520} mx='auto'>
              {sfyr('landing.{lang}.blog.subtitle', {
                defaults: {
                  'landing.{lang=en}.blog.subtitle': 'Notes on software, technology, and what we are working on.',
                },
              })}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing='lg'>
              {featuredPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`${langPrefix}/blog/${post.slug}`}
                  style={{ textDecoration: 'none', color: theme.v0.col.text.hex }}
                >
                  <BlogCard
                    style={{
                      border: `1px solid ${theme.v0.col.backgroundTertiary.hex}`,
                      backgroundColor: theme.v0.col.backgroundSecondary.hex,
                    }}
                  >
                    {post.image && (
                      <img
                        src={post.image}
                        alt={post.title}
                        style={{ width: '100%', height: 180, objectFit: 'cover' }}
                      />
                    )}
                    <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <Title order={4} style={{ lineHeight: 1.3 }}>{post.title}</Title>
                      <Text c={theme.v0.col.textSecondary.hex} size='sm' style={{ flex: 1, lineHeight: 1.5 }}>
                        {post.description}
                      </Text>
                      <Group gap='xs'>
                        <Badge size='xs' variant='light'>
                          {post.author}
                        </Badge>
                        <Text size='xs' c={theme.v0.col.textDimmed.hex}>
                          {new Date(post.date).toLocaleDateString(post.lang === 'it' ? 'it-IT' : 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Text>
                      </Group>
                    </div>
                  </BlogCard>
                </Link>
              ))}
            </SimpleGrid>
            {totalPostCount > featuredPosts.length && (
              <Group justify='center' mt='xl'>
                <Link to={`${langPrefix}/blog`} style={{ textDecoration: 'none' }}>
                  <Button variant='subtle' color={theme.v0.col.primary.hex}>
                    {sfyr('landing.{lang}.blog.view_all', {
                      defaults: { 'landing.{lang=en}.blog.view_all': 'View all posts' },
                    })}
                  </Button>
                </Link>
              </Group>
            )}
          </Container>
        </SectionPadded>
      )}

      <SectionPadded id='contact'>
        <Container size='lg'>
          <BackgroundGradient className='rounded-3xl p-8 md:p-12' containerClassName='mx-auto'>
            <Stack align='center' gap='lg'>
              <IconMessageChatbot size={48} color='white' />
              <Title order={2} ta='center' c='white'>
                {sfyr('landing.{lang}.cta.title', {
                  defaults: { 'landing.{lang=en}.cta.title': 'Have a project in mind?' },
                })}
              </Title>
              <CtaSubtitle ta='center' c='gray.3' maw={480}>
                {sfyr('landing.{lang}.cta.subtitle', {
                  defaults: {
                    'landing.{lang=en}.cta.subtitle': 'Get in touch. We will send you a clear quote with timelines.',
                  },
                })}
              </CtaSubtitle>
              <Group gap='md' mt='md'>
                <a href='mailto:info@epilogo.co' style={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <HoverBorderGradient
                    containerClassName='rounded-full'
                    className='flex items-center gap-2 px-8 py-3 cursor-pointer'
                  >
                    <IconMail size={18} />
                    <span className='text-base font-medium'>
                      {sfyr('landing.{lang}.cta.button', {
                        defaults: { 'landing.{lang=en}.cta.button': 'Contact us' },
                      })}
                    </span>
                  </HoverBorderGradient>
                </a>
              </Group>
            </Stack>
          </BackgroundGradient>
        </Container>
      </SectionPadded>
    </div>
  );
};
